import React, { useState, useEffect, useCallback } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useAuthz, ROLES } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { useConfirm } from "context/ConfirmContext";
import { listTasks, updateTask, generateTasks, deleteTask } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

// ── Constants ─────────────────────────────────────────────────────────────────

const TASK_TYPE_LABELS = {
  ACCOUNTS_PREPARATION:   "Accounts Prep",
  CT600:                  "CT600",
  CONFIRMATION_STATEMENT: "Confirmation Statement",
};

const TASK_TYPE_ICONS = {
  ACCOUNTS_PREPARATION:   "📋",
  CT600:                  "🧾",
  CONFIRMATION_STATEMENT: "✅",
};

const STATUS_OPTIONS = ["pending", "in_progress", "completed", "cancelled"];
const STATUS_LABELS  = { pending: "Pending", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled", overdue: "Overdue" };

const PRIORITY_COLOURS = {
  overdue:  { bg: "rgba(244,67,54,0.12)",  border: "rgba(244,67,54,0.4)",  text: "#b71c1c",  label: "Overdue" },
  critical: { bg: "rgba(244,67,54,0.08)",  border: "rgba(244,67,54,0.3)",  text: "#c62828",  label: "Critical" },
  high:     { bg: "rgba(255,152,0,0.10)",  border: "rgba(255,152,0,0.3)",  text: "#bf360c",  label: "High" },
  medium:   { bg: "rgba(255,193,7,0.10)",  border: "rgba(255,193,7,0.3)",  text: "#e65100",  label: "Medium" },
  low:      { bg: "rgba(76,175,80,0.08)",  border: "rgba(76,175,80,0.25)", text: "#1b5e20",  label: "Low" },
};

const STATUS_COLOURS = {
  pending:     { bg: "rgba(0,87,216,0.07)",   border: "rgba(0,87,216,0.2)",    text: "#0057d8",  dot: "#90caf9" },
  in_progress: { bg: "rgba(0,117,255,0.08)",  border: "rgba(0,117,255,0.25)",  text: "#0041a8",  dot: "#0075ff" },
  completed:   { bg: "rgba(76,175,80,0.08)",  border: "rgba(76,175,80,0.25)",  text: "#1b5e20",  dot: "#66bb6a" },
  cancelled:   { bg: "rgba(0,0,0,0.04)",      border: "rgba(0,0,0,0.1)",       text: "#7a94b0",  dot: "rgba(255,255,255,0.2)" },
  overdue:     { bg: "rgba(244,67,54,0.08)",  border: "rgba(244,67,54,0.3)",   text: "#b71c1c",  dot: "#f44336" },
};

const STATUS_DOT_GLOW = {
  pending:     "0 0 6px 1px rgba(144,202,249,0.55)",
  in_progress: "0 0 6px 1px rgba(0,117,255,0.7)",
  completed:   "0 0 6px 1px rgba(102,187,106,0.55)",
  cancelled:   "none",
  overdue:     "0 0 6px 1px rgba(244,67,54,0.7)",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function daysUntil(dueDateStr) {
  if (!dueDateStr) return null;
  const due   = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((due - today) / 86400000);
}

function formatDueDate(dueDateStr) {
  if (!dueDateStr) return "—";
  return new Date(dueDateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function computedPriority(task) {
  if (task.priority === "overdue") return "overdue";
  if (task.status === "completed" || task.status === "cancelled") return task.priority || "low";
  const days = daysUntil(task.dueDate);
  if (days === null) return task.priority || "low";
  if (days < 0)   return "overdue";
  if (days <= 30) return "critical";
  if (days <= 60) return "high";
  if (days <= 120) return "medium";
  return "low";
}

function computedStatus(task) {
  if (task.status === "completed" || task.status === "cancelled") return task.status;
  const days = daysUntil(task.dueDate);
  if (days !== null && days < 0) return "overdue";
  return task.status;
}

// ── Task card ─────────────────────────────────────────────────────────────────

function TaskCard({ task, onStatusChange, onNavigate, onViewJob, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  const [updating, setUpdating] = useState(false);
  const confirm = useConfirm();
  const [deleting, setDeleting] = useState(false);

  const priority  = computedPriority(task);
  const pc        = PRIORITY_COLOURS[priority] || PRIORITY_COLOURS.low;
  const effStatus = computedStatus(task);
  const sc        = STATUS_COLOURS[effStatus] || STATUS_COLOURS.pending;
  const rawSc     = STATUS_COLOURS[task.status] || STATUS_COLOURS.pending;
  const days      = daysUntil(task.dueDate);
  const typeIcon  = TASK_TYPE_ICONS[task.type] || "📌";
  const typeLabel = TASK_TYPE_LABELS[task.type] || task.type;

  const handleStatusSelect = async (newStatus) => {
    setAnchor(null);
    if (newStatus === task.status) return;
    setUpdating(true);
    try {
      await onStatusChange(task.id, newStatus);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete "${task.title}"`,
      message: `Permanently delete this task?\nThis cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await onDelete(task.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <VuiBox
      sx={{
        background: "#f4f8ff",
        border: `1px solid ${pc.border}`,
        borderRadius: "12px",
        p: 2,
        mb: 1.5,
        opacity: task.status === "cancelled" ? 0.5 : 1,
        transition: "border-color 0.15s",
        "&:hover": { background: "#f4f8ff" },
      }}
    >
      {/* Header row */}
      <VuiBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
        <VuiBox display="flex" alignItems="center" gap={0.8} flex={1} minWidth={0}>
          {/* Status bubble */}
          <Tooltip title={STATUS_LABELS[effStatus] || effStatus} placement="top" arrow>
            <span
              style={{
                flexShrink: 0,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: sc.dot,
                boxShadow: STATUS_DOT_GLOW[effStatus] || "none",
                display: "inline-block",
                cursor: "default",
              }}
            />
          </Tooltip>
          <span style={{ fontSize: "1rem", flexShrink: 0 }}>{typeIcon}</span>
          <VuiTypography
            variant="caption"
           
            fontWeight="bold"
            sx={{ color: TC.primary,  fontSize: "0.8rem", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {task.title}
          </VuiTypography>
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
          <Tooltip title="Go to client profile">
            <IconButton
              size="small"
              sx={{ color: TC.secondary, p: 0.3, "&:hover": { color: TC.link } }}
              onClick={() => onNavigate(task.clientId)}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete task">
            <span>
              <IconButton
                size="small"
                disabled={deleting}
                sx={{ color: TC.secondary, p: 0.3, "&:hover": { color: "#f44336" } }}
                onClick={handleDelete}
              >
                {deleting
                  ? <CircularProgress size={12} sx={{ color: "#f44336" }} />
                  : <DeleteOutlineIcon sx={{ fontSize: 14 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </VuiBox>
      </VuiBox>

      {/* Client name */}
      <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.72rem", display: "block", mb: 1 }}>
        {task.clientName}
      </VuiTypography>

      {/* Tags row */}
      <VuiBox display="flex" alignItems="center" gap={0.8} flexWrap="wrap" mb={1}>
        {/* Type chip */}
        <VuiBox px={0.8} py={0.2} sx={{ background: "#e8f0fe", border: "1px solid #c2d7fb", borderRadius: "6px" }}>
          <VuiTypography sx={{ color: TC.primary, fontSize: "0.65rem", fontWeight: "bold" }}>{typeLabel}</VuiTypography>
        </VuiBox>
        {/* Priority */}
        <VuiBox px={0.8} py={0.2} sx={{ background: pc.bg, border: `1px solid ${pc.border}`, borderRadius: "6px" }}>
          <VuiTypography sx={{ color: pc.text, fontSize: "0.65rem", fontWeight: "bold" }}>{pc.label}</VuiTypography>
        </VuiBox>
        {/* Linked job */}
        {task.jobId && (
          <VuiBox
            px={0.8} py={0.2}
            onClick={(e) => { e.stopPropagation(); onViewJob && onViewJob(task.clientId); }}
            sx={{
              background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.3)",
              borderRadius: "6px", cursor: "pointer",
              "&:hover": { background: "rgba(0,117,255,0.18)" },
            }}
          >
            <VuiTypography sx={{ color: TC.link, fontSize: "0.65rem" }}>📋 View Job</VuiTypography>
          </VuiBox>
        )}
      </VuiBox>

      {/* Due date */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between">
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: "0.7rem" }}>
          Due: {formatDueDate(task.dueDate)}
          {days !== null && task.status !== "completed" && task.status !== "cancelled" && (
            <span style={{ color: pc.text, marginLeft: 6 }}>
              {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "today" : `${days}d`}
            </span>
          )}
        </VuiTypography>

        {/* Status button */}
        <VuiBox
          onClick={(e) => setAnchor(e.currentTarget)}
          px={1} py={0.3}
          sx={{
            background: rawSc.bg,
            border: `1px solid ${rawSc.border}`,
            borderRadius: "6px",
            cursor: updating ? "default" : "pointer",
            display: "flex", alignItems: "center", gap: 0.5,
            "&:hover": { opacity: 0.85 },
          }}
        >
          {updating
            ? <CircularProgress size={10} sx={{ color: rawSc.text }} />
            : <VuiTypography sx={{ color: rawSc.text, fontSize: "0.68rem" }}>{STATUS_LABELS[task.status] || task.status}</VuiTypography>
          }
        </VuiBox>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          PaperProps={{ sx: { background: "#1a2035", border: "1px solid #d8e6f7", borderRadius: "10px", minWidth: 150 } }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem
              key={s}
              onClick={() => handleStatusSelect(s)}
              selected={s === task.status}
              sx={{ color: s === task.status ? "#90caf9" : "rgba(255,255,255,0.7)", fontSize: "0.8rem", py: 0.8 }}
            >
              {STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </Menu>
      </VuiBox>
    </VuiBox>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────

function TaskColumn({ title, colour, tasks, emptyMsg, onStatusChange, onNavigate, onViewJob, onDelete }) {
  return (
    <VuiBox>
      <VuiBox display="flex" alignItems="center" gap={1} mb={2}>
        <VuiTypography variant="caption" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.07em" }}>
          {title}
        </VuiTypography>
        <VuiBox
          px={1} py={0.2}
          sx={{ background: colour.bg, border: `1px solid ${colour.border}`, borderRadius: "12px" }}
        >
          <VuiTypography sx={{ color: colour.text, fontSize: "0.68rem", fontWeight: "bold" }}>{tasks.length}</VuiTypography>
        </VuiBox>
      </VuiBox>

      {tasks.length === 0 ? (
        <VuiTypography variant="caption" sx={{ color: TC.tertiary, fontSize: "0.73rem" }}>{emptyMsg}</VuiTypography>
      ) : (
        tasks.map((t) => (
          <TaskCard key={t.id} task={t} onStatusChange={onStatusChange} onNavigate={onNavigate} onViewJob={onViewJob} onDelete={onDelete} />
        ))
      )}
    </VuiBox>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TYPE_FILTER_OPTIONS = [
  { value: "", label: "All types" },
  { value: "ACCOUNTS_PREPARATION",   label: "📋 Accounts Prep" },
  { value: "CT600",                  label: "🧾 CT600" },
  { value: "CONFIRMATION_STATEMENT", label: "✅ Confirmation Statement" },
];

export default function TasksPage() {
  const history = useHistory();
  const { search } = useLocation();
  const { isSuperAdmin, isTenantAdmin, isUser, tenantId: authTenantId } = useAuthz();
  const { selectedCompanyId } = useCompany();

  const tenantId = isSuperAdmin ? (selectedCompanyId || "") : (authTenantId || "");

  const [tasks,          setTasks]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [generating,     setGenerating]     = useState(false);
  const [typeFilter,     setTypeFilter]     = useState("");
  const [clientIdFilter, setClientIdFilter] = useState("");
  const [genResult,      setGenResult]      = useState(null);

  if (!isSuperAdmin && !isTenantAdmin && !isUser) return <Redirect to="/unauthorized" />;

  // Sync clientId from URL query param (?clientId=xxx)
  useEffect(() => {
    const params = new URLSearchParams(search);
    setClientIdFilter(params.get("clientId") || "");
  }, [search]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const query = {};
      if (typeFilter)     query.type     = typeFilter;
      if (clientIdFilter) query.clientId = clientIdFilter;
      const data = await listTasks(tenantId, query);
      setTasks(data.tasks || []);
    } catch (e) {
      console.error("Failed to load tasks:", e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, typeFilter, clientIdFilter]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(tenantId, taskId, { status: newStatus });
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (e) {
      console.error("Failed to update task:", e);
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await deleteTask(tenantId, taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) {
      console.error("Failed to delete task:", e);
    }
  };

  const handleGenerate = async () => {
    if (!tenantId) return;
    setGenerating(true);
    setGenResult(null);
    try {
      const result = await generateTasks(tenantId);
      setGenResult(result);
      await load();
    } catch (e) {
      console.error("Generate tasks failed:", e);
    } finally {
      setGenerating(false);
    }
  };

  // Sort tasks by dueDate
  const sorted = [...tasks].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  // Bucket into columns
  const overdue    = sorted.filter((t) => {
    if (t.status === "completed" || t.status === "cancelled") return false;
    const days = daysUntil(t.dueDate);
    return days !== null && days < 0;
  });
  const dueSoon    = sorted.filter((t) => {
    if (t.status === "completed" || t.status === "cancelled") return false;
    const days = daysUntil(t.dueDate);
    return days !== null && days >= 0 && days <= 60;
  });
  const upcoming   = sorted.filter((t) => {
    if (t.status === "completed" || t.status === "cancelled") return false;
    const days = daysUntil(t.dueDate);
    return days !== null && days > 60;
  });
  const completed  = sorted.filter((t) => t.status === "completed" || t.status === "cancelled");

  return (
    <DashboardLayout>
      <DashboardNavbar customTitle="Filing Tasks" />
      <VuiBox py={3}>

        {/* Header */}
        <VuiBox mb={3} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h4" fontWeight="bold" sx={{ color: TC.primary }}>📌 Filing Tasks</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.tertiary,  opacity: 0.7 }}>
              Auto-generated from Companies House deadlines — review, assign and track
            </VuiTypography>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {genResult && (
              <VuiTypography variant="caption" sx={{ color: "#81c784", fontSize: "0.78rem" }}>
                ✓ {genResult.tasksCreated} new task{genResult.tasksCreated !== 1 ? "s" : ""} created
              </VuiTypography>
            )}

            <Tooltip title="Refresh task list">
              <span>
                <IconButton aria-label="Refresh task list" onClick={load} disabled={loading} sx={{ color: TC.secondary, "&:hover": { color: "#fff" } }}>
                  <RefreshIcon aria-hidden="true" fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <VuiButton
              color="info"
              variant="contained"
              size="small"
              onClick={handleGenerate}
              disabled={generating || !tenantId}
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon fontSize="small" />}
              sx={{ fontSize: "0.78rem" }}
            >
              {generating ? "Generating…" : "Generate Tasks"}
            </VuiButton>
          </VuiBox>
        </VuiBox>

        {!tenantId && (
          <VuiBox px={2.5} py={2} sx={{ background: "rgba(255,152,0,0.08)", border: "1px solid rgba(255,152,0,0.35)", borderRadius: "10px", mb: 3 }}>
            <VuiTypography variant="caption" sx={{ color: "#ffb74d" }}>
              ⚠️ Please select a company from the top bar to view tasks.
            </VuiTypography>
          </VuiBox>
        )}

        {/* Client filter banner */}
        {clientIdFilter && (
          <VuiBox
            display="flex" alignItems="center" gap={1} px={2} py={1.2} mb={2}
            sx={{ background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.25)", borderRadius: "10px" }}
          >
            <VuiTypography sx={{ color: TC.link, fontSize: "0.78rem" }}>
              🔗 Filtered for client: <strong>{tasks[0]?.clientName || clientIdFilter}</strong>
            </VuiTypography>
            <Chip
              label="Show all"
              size="small"
              onClick={() => { setClientIdFilter(""); history.replace("/tasks"); }}
              sx={{ cursor: "pointer", bgcolor: "rgba(0,117,255,0.15)", color: TC.link, fontSize: "0.68rem", height: 20, ml: 1 }}
            />
          </VuiBox>
        )}

        {/* Type filter chips */}
        <VuiBox display="flex" gap={1} mb={3} flexWrap="wrap">
          {TYPE_FILTER_OPTIONS.map(({ value, label }) => (
            <VuiBox
              key={value}
              px={1.5} py={0.5}
              onClick={() => setTypeFilter(value)}
              sx={{
                background: typeFilter === value ? "rgba(0,87,216,0.12)" : "#ffffff",
                border: `1px solid ${typeFilter === value ? "#0057d8" : "#c2d7fb"}`,
                borderRadius: "20px",
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { background: "rgba(0,87,216,0.07)" },
              }}
            >
              <VuiTypography sx={{ color: typeFilter === value ? "#0041a8" : TC.secondary, fontSize: "0.73rem", fontWeight: typeFilter === value ? "600" : "400" }}>
                {label}
              </VuiTypography>
            </VuiBox>
          ))}
        </VuiBox>

        {loading ? (
          <VuiBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} sx={{ color: TC.linkHover }} />
          </VuiBox>
        ) : (
          <Grid container spacing={3}>
            {/* Overdue */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", p: 2.5, borderRadius: "16px" }}>
                <TaskColumn
                  title="🔴 Overdue"
                  colour={{ bg: "rgba(244,67,54,0.1)", border: "rgba(244,67,54,0.3)", text: "#b71c1c" }}
                  tasks={overdue}
                  emptyMsg="No overdue tasks"
                  onStatusChange={handleStatusChange}
                  onNavigate={(clientId) => history.push(`/client-profiles/${clientId}`)}
                  onViewJob={(clientId) => history.push(`/jobs-board?clientId=${encodeURIComponent(clientId)}`)}
                  onDelete={handleDelete}
                />
              </Card>
            </Grid>

            {/* Due soon */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", p: 2.5, borderRadius: "16px" }}>
                <TaskColumn
                  title="⚠️ Due Soon"
                  colour={{ bg: "rgba(255,152,0,0.1)", border: "rgba(255,152,0,0.3)", text: "#bf360c" }}
                  tasks={dueSoon}
                  emptyMsg="Nothing due in the next 60 days"
                  onStatusChange={handleStatusChange}
                  onNavigate={(clientId) => history.push(`/client-profiles/${clientId}`)}
                  onViewJob={(clientId) => history.push(`/jobs-board?clientId=${encodeURIComponent(clientId)}`)}
                  onDelete={handleDelete}
                />
              </Card>
            </Grid>

            {/* Upcoming */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", p: 2.5, borderRadius: "16px" }}>
                <TaskColumn
                  title="📅 Upcoming"
                  colour={{ bg: "rgba(0,87,216,0.07)", border: "rgba(0,87,216,0.2)", text: "#0057d8" }}
                  tasks={upcoming}
                  emptyMsg="No upcoming tasks"
                  onStatusChange={handleStatusChange}
                  onNavigate={(clientId) => history.push(`/client-profiles/${clientId}`)}
                  onViewJob={(clientId) => history.push(`/jobs-board?clientId=${encodeURIComponent(clientId)}`)}
                  onDelete={handleDelete}
                />
              </Card>
            </Grid>

            {/* Done / cancelled */}
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", p: 2.5, borderRadius: "16px" }}>
                <TaskColumn
                  title="✓ Done"
                  colour={{ bg: "rgba(76,175,80,0.08)", border: "rgba(76,175,80,0.2)", text: "#1b5e20" }}
                  tasks={completed}
                  emptyMsg="No completed tasks"
                  onStatusChange={handleStatusChange}
                  onNavigate={(clientId) => history.push(`/client-profiles/${clientId}`)}
                  onViewJob={(clientId) => history.push(`/jobs-board?clientId=${encodeURIComponent(clientId)}`)}
                  onDelete={handleDelete}
                />
              </Card>
            </Grid>
          </Grid>
        )}

        {!loading && tasks.length === 0 && tenantId && (
          <VuiBox textAlign="center" py={6}>
            <VuiTypography variant="h6" sx={{ color: TC.tertiary, mb: 1 }}>No tasks yet</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.tertiary }}>
              Click "Generate Tasks" to create tasks from your clients' Companies House filing deadlines.
            </VuiTypography>
          </VuiBox>
        )}

      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

